import { bytesToBase64, base64ToBytes, toArrayBuffer } from './encoding';
import type { CreateCallRuntimeTokenResponse, IdentityId, CallSessionId, PublicKey, VaultContact } from '@shared/types';

declare global {
  interface Window {
    AndroidKeyBridge?: {
      getSessionToken(): string;
      hasKeys(): boolean;
      getStoredKeys(token: string): string;
      saveKeys(token: string, keysJson: string): boolean;
      saveChatEpochKey?(token: string, scope: 'group' | 'direct', chatId: string, epoch: number, rawKeyBase64: string): boolean;
      getChatEpochKey?(token: string, scope: 'group' | 'direct', chatId: string, epoch: number): string;
      deleteChatEpochKey?(token: string, scope: 'group' | 'direct', chatId: string, epoch: number): boolean;
      clearLocalProfileKeyMaterial?(token: string): boolean;
      saveTemporaryMembership?(token: string, enrollmentId: string, recordJson: string): boolean;
      getTemporaryMemberships?(token: string): string;
      deleteTemporaryMembership?(token: string, enrollmentId: string): boolean;
      refreshMobilePushRegistration?(token: string): boolean;
      saveMobileRefreshGrant?(token: string, grantJson: string): boolean;
      getMobileRefreshGrant?(token: string): string;
      clearMobileRefreshGrant?(token: string): boolean;
      saveMobileServerTokenValidity?(token: string, validityJson: string): boolean;
      getMobileServerTokenValidity?(token: string): string;
      consumeMobileHandoff(token: string): string;
      saveCurrentUrl(token: string, url: string): boolean;
      getAppLanguage?(): string;
      setAppLanguage?(language: string): boolean;
    };
    AndroidCallBridge?: {
      getCapabilities?(): string;
      hasMultipleCameras?(): boolean;
      hasWebMediaPermissions?(audio: boolean, video: boolean): boolean;
      ensureWebMediaPermissions(audio: boolean, video: boolean): string;
      startOutgoingVideoCall?(requestJson: string): string;
      answerIncomingVideoCall?(requestJson: string): string;
      endCall(callSessionId: string): void;
      setMuted(callSessionId: string, muted: boolean): void;
      setLocalVideoEnabled?(callSessionId: string, enabled: boolean): void;
      switchCamera?(callSessionId: string): string;
      stopIncomingRinging?(callSessionId?: string): void;
      getAudioRoutes(callSessionId: string): string;
      setAudioRoute(callSessionId: string, routeId: string): string;
      setNativeAudioRouteDialogOpen?(open: boolean): void;
      resumeNativeCall?(): void;
      consumeNativeCallMinimizedLaunchPending?(): boolean;
      getActiveCalls?(): string;
      prepareWebMediaCapture?(audio: boolean, video: boolean): string;
    };
    AndroidContactBridge?: {
      upsertContact(contactJson: string): boolean;
      upsertAvatar?(serverId: string, serverUrl: string, identityId: string, avatarDataUrl: string): boolean;
      deleteContact(contactId: string): boolean;
      replaceAllContacts(contactsJson: string): boolean;
      clearAllContacts?(): boolean;
    };
    AndroidChatBridge?: {
      saveDirectMessages?(token: string, messagesJson: string): boolean;
      getDirectThreadSummariesPage?(token: string, optionsJson: string): string;
      getDirectThreadMessagesPage?(token: string, threadKey: string, optionsJson: string): string;
      getDirectMessageById?(token: string, serverMessageId: string): string;
      getFailedDirectMessages?(token: string): string;
      updateDirectMessageStatus?(token: string, serverMessageId: string, status: string): boolean;
      deleteDirectMessage?(token: string, serverMessageId: string): boolean;
      deleteDirectThreadMessages?(token: string, currentIdentityId: string, peerIdentityId: string): boolean;
      markDirectThreadReadUpTo?(token: string, localIdentityId: string, peerIdentityId: string, readThrough: number): boolean;
      clearDirectMessages?(token: string): boolean;
      saveGroupChats?(token: string, chatsJson: string): boolean;
      getGroupChats?(token: string): string;
      saveGroupMessages?(token: string, messagesJson: string): boolean;
      getGroupMessages?(token: string, chatId: string): string;
      getLatestGroupMessage?(token: string, chatId: string): string;
      deleteGroupMessages?(token: string, chatId: string): boolean;
      deleteGroupMessage?(token: string, messageId: string): boolean;
      deleteGroupChat?(token: string, chatId: string): boolean;
      deleteGroupParticipants?(token: string, chatId: string): boolean;
      clearGroupMessages?(token: string): boolean;
      clearGroupChats?(token: string): boolean;
      clearGroupParticipants?(token: string): boolean;
      clearLocalProfileData?(token: string): boolean;
      saveGroupParticipants?(token: string, chatId: string, participantsJson: string): boolean;
      getGroupParticipants?(token: string, chatId: string): string;
      saveArchiveWriterCheckpoint?(token: string, checkpointId: string, checkpointJson: string): boolean;
      getArchiveWriterCheckpoint?(token: string, checkpointId: string): string;
      deleteArchiveWriterCheckpoint?(token: string, checkpointId: string): boolean;
      deleteArchiveWriterCheckpointsForIdentity?(token: string, sourceCircleIdentityId: string): boolean;
      saveArchiveCacheEntry?(token: string, cacheId: string, cacheJson: string): boolean;
      getArchiveCacheEntry?(token: string, cacheId: string): string;
      deleteArchiveCacheEntry?(token: string, cacheId: string): boolean;
      deleteArchiveCacheForArchive?(token: string, sourceCircleIdentityId: string, archiveId: string): boolean;
      deleteArchiveCacheForIdentity?(token: string, sourceCircleIdentityId: string): boolean;
    };
    AndroidUiBridge?: {
      setToolbarVisible?(visible: boolean): boolean;
      saveFileAs?(fileName: string, mimeType: string, base64Data: string): boolean;
      openFile?(fileName: string, mimeType: string, base64Data: string): boolean;
      openUrl?(url: string): boolean;
      consumePendingShare?(): string;
      clearPendingShare?(shareId: string): boolean;
      startVoiceMessageRecording?(): string;
      stopVoiceMessageRecording?(): string;
      cancelVoiceMessageRecording?(): boolean;
      getWebBundleStatus?(): string;
      refreshWebBundle?(): boolean;
      clearMissedCallNotifications?(callSessionIdsJson: string): boolean;
      clearMissedCallerBadges?(callerIdentityIdsJson: string): boolean;
    };
    __circlusHandleNativeBack?: () => 'handled' | 'exit';
  }
}

export const isWebViewMode: boolean = navigator.userAgent.includes('CirclusApp');

export type NativeWebBundleStatus = {
  version: string;
  source: 'bundled' | 'downloaded' | string;
  installedAt: number;
  generatedAt: string;
  updateState: 'idle' | 'checking' | 'downloading' | 'installing' | 'error' | string;
  pendingVersion: string;
  pendingGeneratedAt: string;
  downloadedFiles: number;
  totalFiles: number;
  lastError: string;
};

export function getNativeWebBundleStatus(): NativeWebBundleStatus | null {
  if (!isWebViewMode || !window.AndroidUiBridge?.getWebBundleStatus) return null;
  try {
    const raw = window.AndroidUiBridge.getWebBundleStatus();
    const parsed = JSON.parse(raw || '{}');
    return {
      version: String(parsed.version || ''),
      source: String(parsed.source || ''),
      installedAt: Number(parsed.installedAt || 0),
      generatedAt: String(parsed.generatedAt || ''),
      updateState: String(parsed.updateState || 'idle'),
      pendingVersion: String(parsed.pendingVersion || ''),
      pendingGeneratedAt: String(parsed.pendingGeneratedAt || ''),
      downloadedFiles: Number(parsed.downloadedFiles || 0),
      totalFiles: Number(parsed.totalFiles || 0),
      lastError: String(parsed.lastError || '')
    };
  } catch {
    return null;
  }
}

export function refreshNativeWebBundle(): boolean {
  if (!isWebViewMode || typeof window.AndroidUiBridge?.refreshWebBundle !== 'function') return false;
  try {
    return window.AndroidUiBridge.refreshWebBundle() === true;
  } catch {
    return false;
  }
}

export function setNativeToolbarVisible(visible: boolean): void {
  if (!isWebViewMode) return;
  try {
    window.AndroidUiBridge?.setToolbarVisible?.(visible);
  } catch {
    // Native UI sync is best-effort; web layout remains functional without it.
  }
}

export function clearNativeMissedCallNotifications(callSessionIds: string[]): boolean {
  if (!isWebViewMode || typeof window.AndroidUiBridge?.clearMissedCallNotifications !== 'function') return false;
  const ids = Array.from(new Set(callSessionIds.map((id) => String(id || '').trim()).filter(Boolean)));
  if (ids.length === 0) return false;
  try {
    return window.AndroidUiBridge.clearMissedCallNotifications(JSON.stringify(ids)) === true;
  } catch {
    return false;
  }
}

export function clearNativeMissedCallerBadges(callerIdentityIds: string[]): boolean {
  if (!isWebViewMode || typeof window.AndroidUiBridge?.clearMissedCallerBadges !== 'function') return false;
  const ids = Array.from(new Set(callerIdentityIds.map((id) => String(id || '').trim()).filter(Boolean)));
  if (ids.length === 0) return false;
  try {
    return window.AndroidUiBridge.clearMissedCallerBadges(JSON.stringify(ids)) === true;
  } catch {
    return false;
  }
}

export async function saveBlobWithNativePicker(blob: Blob, fileName: string): Promise<boolean> {
  if (!isWebViewMode || !window.AndroidUiBridge?.saveFileAs) return false;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const mimeType = blob.type || 'application/octet-stream';
  try {
    return window.AndroidUiBridge.saveFileAs(fileName, mimeType, bytesToBase64(bytes)) === true;
  } catch {
    return false;
  }
}

export function openExternalUrl(url: string): void {
  if (isWebViewMode && window.AndroidUiBridge?.openUrl) {
    try {
      window.AndroidUiBridge.openUrl(url);
      return;
    } catch {
      // fall through to window.open
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function openBlobWithSystemApp(blob: Blob, fileName: string): Promise<boolean> {
  const mimeType = blob.type || 'application/octet-stream';
  if (isWebViewMode && window.AndroidUiBridge?.openFile) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    try {
      return window.AndroidUiBridge.openFile(fileName, mimeType, bytesToBase64(bytes)) === true;
    } catch {
      return false;
    }
  }

  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return Boolean(opened);
}

export type NativeVoiceMessageRecording = {
  file: File;
  durationMs: number;
};

export function canUseNativeVoiceMessageRecorder(): boolean {
  return isWebViewMode
    && typeof window.AndroidUiBridge?.startVoiceMessageRecording === 'function'
    && typeof window.AndroidUiBridge?.stopVoiceMessageRecording === 'function';
}

function parseNativeRecorderResult(raw: string): any {
  const parsed = JSON.parse(raw || '{}');
  if (parsed?.ok === false) {
    throw new Error(String(parsed.error || 'Native recorder request failed'));
  }
  return parsed;
}

export function startNativeVoiceMessageRecording(): void {
  const raw = window.AndroidUiBridge?.startVoiceMessageRecording?.();
  parseNativeRecorderResult(raw || JSON.stringify({ ok: false, error: 'Native recorder is not available' }));
}

export function stopNativeVoiceMessageRecording(): NativeVoiceMessageRecording {
  const raw = window.AndroidUiBridge?.stopVoiceMessageRecording?.();
  const parsed = parseNativeRecorderResult(raw || JSON.stringify({ ok: false, error: 'Native recorder is not available' }));
  const fileName = String(parsed.fileName || `voice-message-${Date.now()}.m4a`);
  const mimeType = String(parsed.mimeType || 'audio/mp4');
  const bytes = base64ToBytes(String(parsed.base64Data || ''));
  return {
    file: new File([toArrayBuffer(bytes)], fileName, { type: mimeType }),
    durationMs: Number(parsed.durationMs || 0)
  };
}

export function cancelNativeVoiceMessageRecording(): void {
  window.AndroidUiBridge?.cancelVoiceMessageRecording?.();
}

export type NativeSharedFile = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  url: string;
};

export type NativeSharePayload = {
  id: string;
  text?: string;
  files: NativeSharedFile[];
};

export function consumePendingNativeShare(): NativeSharePayload | null {
  if (!isWebViewMode || !window.AndroidUiBridge?.consumePendingShare) return null;
  try {
    const raw = window.AndroidUiBridge.consumePendingShare();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NativeSharePayload>;
    const id = String(parsed.id || '').trim();
    if (!id) return null;
    const files = Array.isArray(parsed.files)
      ? parsed.files.map((file) => ({
          id: String(file?.id || '').trim(),
          fileName: String(file?.fileName || 'shared-file').trim() || 'shared-file',
          mimeType: String(file?.mimeType || 'application/octet-stream').trim() || 'application/octet-stream',
          sizeBytes: Number(file?.sizeBytes || 0),
          url: String(file?.url || '').trim()
        })).filter((file) => file.id && file.url)
      : [];
    const text = String(parsed.text || '').trim();
    if (!text && files.length === 0) return null;
    return { id, ...(text ? { text } : {}), files };
  } catch {
    return null;
  }
}

export function clearPendingNativeShare(shareId: string): boolean {
  if (!isWebViewMode || !window.AndroidUiBridge?.clearPendingShare) return false;
  try {
    return window.AndroidUiBridge.clearPendingShare(shareId) === true;
  } catch {
    return false;
  }
}

export type NativeBridgeCapability =
  | 'nativeEncryptedPushV1'
  | 'nativeContactIndexV1'
  | 'nativeChatEpochKeysV1'
  | 'nativeTemporaryMembershipsV1'
  | 'nativeMobilePushRuntimeV1'
  | 'nativeChatStorageV1'
  | 'nativeArchiveCheckpointV1'
  | 'nativeArchiveCacheV1';

type NativeBridgeCapabilitiesPayload = {
  bridgeContractVersion: number;
  capabilities: NativeBridgeCapability[];
};

export interface BridgeIdentityEntry {
  id: string;
  seed: string; // base64 Ed25519 seed
  publicKey: string; // base64 Ed25519 public key
  serverUrl: string; // URL of the circles server
  serverName?: string;
  identityName?: string;
  publishIdentity?: boolean;
  presenceVisible?: boolean;
  role?: string;
  accessKind?: 'direct_guest' | 'group_guest';
  accessContextLabel?: string;
  vpsId?: string;
  isServerAdmin?: boolean;
  isPrimaryVpsTarget?: boolean;
  fingerprint?: string;
  serverId?: string;
  noNamesOnServer?: boolean;
  useForBackupStorage?: boolean;
  pendingRegistration?: boolean;
}

export interface BridgeDeviceEntry {
  deviceId: string;
  identityId: string;
  publicKey: string;
  seed: string;
  label?: string;
  encryptionPublicKey?: string;
  encryptionSeed?: string;
  pendingRegistration?: boolean;
}

export interface BridgeKeyData {
  masterKey: string; // base64 raw AES-GCM-256 bytes; may be empty until master key is created/imported
  identities: Array<BridgeIdentityEntry>;
  devices: BridgeDeviceEntry[];
}

export interface BridgeMobileHandoffData {
  handoffId: string;
  handoffCode: string;
}

// Cached session token — retrieved once per app session.
let _sessionToken: string | null = null;
let _nativeBridgeCapabilities: NativeBridgeCapabilitiesPayload | null = null;

const KNOWN_NATIVE_BRIDGE_CAPABILITIES = new Set<NativeBridgeCapability>([
  'nativeEncryptedPushV1',
  'nativeContactIndexV1',
  'nativeChatEpochKeysV1',
  'nativeTemporaryMembershipsV1',
  'nativeMobilePushRuntimeV1',
  'nativeChatStorageV1',
  'nativeArchiveCheckpointV1',
  'nativeArchiveCacheV1'
]);

function getSessionToken(): string {
  if (_sessionToken !== null) return _sessionToken;
  _sessionToken = window.AndroidKeyBridge?.getSessionToken() ?? '';
  return _sessionToken;
}

function readNativeBridgeCapabilities(): NativeBridgeCapabilitiesPayload {
  if (!isWebViewMode) {
    return { bridgeContractVersion: 0, capabilities: [] };
  }
  if (_nativeBridgeCapabilities) return _nativeBridgeCapabilities;

  try {
    const raw = window.AndroidCallBridge?.getCapabilities?.() || '';
    if (!raw) {
      _nativeBridgeCapabilities = { bridgeContractVersion: 0, capabilities: [] };
      return _nativeBridgeCapabilities;
    }
    const parsed = JSON.parse(raw) as {
      bridgeContractVersion?: unknown;
      capabilities?: unknown;
    };
    const bridgeContractVersion = Number(parsed?.bridgeContractVersion || 0);
    const capabilities = Array.isArray(parsed?.capabilities)
      ? parsed.capabilities
        .map((value) => String(value || '').trim())
        .filter((value): value is NativeBridgeCapability => KNOWN_NATIVE_BRIDGE_CAPABILITIES.has(value as NativeBridgeCapability))
      : [];

    _nativeBridgeCapabilities = {
      bridgeContractVersion: Number.isFinite(bridgeContractVersion) ? bridgeContractVersion : 0,
      capabilities
    };
    return _nativeBridgeCapabilities;
  } catch {
    _nativeBridgeCapabilities = { bridgeContractVersion: 0, capabilities: [] };
    return _nativeBridgeCapabilities;
  }
}

function hasNativeCapability(capability: NativeBridgeCapability): boolean {
  return readNativeBridgeCapabilities().capabilities.includes(capability);
}

export function canUseNativeChatStorage(): boolean {
  return isWebViewMode
    && hasNativeCapability('nativeChatStorageV1')
    && typeof window.AndroidChatBridge?.saveDirectMessages === 'function'
    && typeof window.AndroidChatBridge?.getDirectThreadSummariesPage === 'function'
    && typeof window.AndroidChatBridge?.saveGroupMessages === 'function'
    && typeof window.AndroidChatBridge?.getGroupMessages === 'function';
}

function callNativeChat<T>(fallback: T, fn: (bridge: NonNullable<Window['AndroidChatBridge']>, token: string) => T): T {
  if (!canUseNativeChatStorage() || !window.AndroidChatBridge) return fallback;
  try {
    return fn(window.AndroidChatBridge, getSessionToken());
  } catch {
    return fallback;
  }
}

export const nativeChatStorage = {
  saveDirectMessages(messagesJson: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.saveDirectMessages?.(token, messagesJson) === true);
  },
  getDirectThreadSummariesPage(optionsJson: string): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getDirectThreadSummariesPage?.(token, optionsJson) || null);
  },
  getDirectThreadMessagesPage(threadKey: string, optionsJson: string): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getDirectThreadMessagesPage?.(token, threadKey, optionsJson) || null);
  },
  getDirectMessageById(serverMessageId: string): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getDirectMessageById?.(token, serverMessageId) || null);
  },
  getFailedDirectMessages(): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getFailedDirectMessages?.(token) || null);
  },
  updateDirectMessageStatus(serverMessageId: string, status: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.updateDirectMessageStatus?.(token, serverMessageId, status) === true);
  },
  deleteDirectMessage(serverMessageId: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.deleteDirectMessage?.(token, serverMessageId) === true);
  },
  deleteDirectThreadMessages(currentIdentityId: string, peerIdentityId: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.deleteDirectThreadMessages?.(token, currentIdentityId, peerIdentityId) === true);
  },
  markDirectThreadReadUpTo(localIdentityId: string, peerIdentityId: string, readThrough: number): boolean {
    return callNativeChat(false, (bridge, token) => bridge.markDirectThreadReadUpTo?.(token, localIdentityId, peerIdentityId, readThrough) === true);
  },
  clearDirectMessages(): boolean {
    return callNativeChat(false, (bridge, token) => bridge.clearDirectMessages?.(token) === true);
  },
  saveGroupChats(chatsJson: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.saveGroupChats?.(token, chatsJson) === true);
  },
  getGroupChats(): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getGroupChats?.(token) || null);
  },
  saveGroupMessages(messagesJson: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.saveGroupMessages?.(token, messagesJson) === true);
  },
  getGroupMessages(chatId: string): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getGroupMessages?.(token, chatId) || null);
  },
  getLatestGroupMessage(chatId: string): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getLatestGroupMessage?.(token, chatId) || null);
  },
  deleteGroupMessages(chatId: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.deleteGroupMessages?.(token, chatId) === true);
  },
  deleteGroupMessage(messageId: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.deleteGroupMessage?.(token, messageId) === true);
  },
  deleteGroupChat(chatId: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.deleteGroupChat?.(token, chatId) === true);
  },
  deleteGroupParticipants(chatId: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.deleteGroupParticipants?.(token, chatId) === true);
  },
  clearGroupMessages(): boolean {
    return callNativeChat(false, (bridge, token) => bridge.clearGroupMessages?.(token) === true);
  },
  clearGroupChats(): boolean {
    return callNativeChat(false, (bridge, token) => bridge.clearGroupChats?.(token) === true);
  },
  clearGroupParticipants(): boolean {
    return callNativeChat(false, (bridge, token) => bridge.clearGroupParticipants?.(token) === true);
  },
  clearLocalProfileData(): boolean {
    return callNativeChat(false, (bridge, token) => bridge.clearLocalProfileData?.(token) === true);
  },
  saveGroupParticipants(chatId: string, participantsJson: string): boolean {
    return callNativeChat(false, (bridge, token) => bridge.saveGroupParticipants?.(token, chatId, participantsJson) === true);
  },
  getGroupParticipants(chatId: string): string | null {
    return callNativeChat<string | null>(null, (bridge, token) => bridge.getGroupParticipants?.(token, chatId) || null);
  }
};

export function canUseNativeArchiveCheckpointStore(): boolean {
  return isWebViewMode
    && hasNativeCapability('nativeArchiveCheckpointV1')
    && typeof window.AndroidChatBridge?.saveArchiveWriterCheckpoint === 'function'
    && typeof window.AndroidChatBridge?.getArchiveWriterCheckpoint === 'function'
    && typeof window.AndroidChatBridge?.deleteArchiveWriterCheckpoint === 'function';
}

function callNativeArchiveCheckpoint<T>(fallback: T, fn: (bridge: NonNullable<Window['AndroidChatBridge']>, token: string) => T): T {
  if (!canUseNativeArchiveCheckpointStore() || !window.AndroidChatBridge) return fallback;
  try {
    return fn(window.AndroidChatBridge, getSessionToken());
  } catch {
    return fallback;
  }
}

export const nativeArchiveCheckpointStore = {
  save(checkpointId: string, checkpointJson: string): boolean {
    return callNativeArchiveCheckpoint(false, (bridge, token) => bridge.saveArchiveWriterCheckpoint?.(token, checkpointId, checkpointJson) === true);
  },
  get(checkpointId: string): string | null {
    return callNativeArchiveCheckpoint<string | null>(null, (bridge, token) => bridge.getArchiveWriterCheckpoint?.(token, checkpointId) || null);
  },
  delete(checkpointId: string): boolean {
    return callNativeArchiveCheckpoint(false, (bridge, token) => bridge.deleteArchiveWriterCheckpoint?.(token, checkpointId) === true);
  },
  deleteForIdentity(sourceCircleIdentityId: string): boolean {
    return callNativeArchiveCheckpoint(false, (bridge, token) => bridge.deleteArchiveWriterCheckpointsForIdentity?.(token, sourceCircleIdentityId) === true);
  }
};

export function canUseNativeArchiveCacheStore(): boolean {
  return isWebViewMode
    && hasNativeCapability('nativeArchiveCacheV1')
    && typeof window.AndroidChatBridge?.saveArchiveCacheEntry === 'function'
    && typeof window.AndroidChatBridge?.getArchiveCacheEntry === 'function'
    && typeof window.AndroidChatBridge?.deleteArchiveCacheEntry === 'function'
    && typeof window.AndroidChatBridge?.deleteArchiveCacheForArchive === 'function';
}

function callNativeArchiveCache<T>(fallback: T, fn: (bridge: NonNullable<Window['AndroidChatBridge']>, token: string) => T): T {
  if (!canUseNativeArchiveCacheStore() || !window.AndroidChatBridge) return fallback;
  try {
    return fn(window.AndroidChatBridge, getSessionToken());
  } catch {
    return fallback;
  }
}

export const nativeArchiveCacheStore = {
  save(cacheId: string, cacheJson: string): boolean {
    return callNativeArchiveCache(false, (bridge, token) => bridge.saveArchiveCacheEntry?.(token, cacheId, cacheJson) === true);
  },
  get(cacheId: string): string | null {
    return callNativeArchiveCache<string | null>(null, (bridge, token) => bridge.getArchiveCacheEntry?.(token, cacheId) || null);
  },
  delete(cacheId: string): boolean {
    return callNativeArchiveCache(false, (bridge, token) => bridge.deleteArchiveCacheEntry?.(token, cacheId) === true);
  },
  deleteForArchive(sourceCircleIdentityId: string, archiveId: string): boolean {
    return callNativeArchiveCache(false, (bridge, token) => bridge.deleteArchiveCacheForArchive?.(token, sourceCircleIdentityId, archiveId) === true);
  },
  deleteForIdentity(sourceCircleIdentityId: string): boolean {
    return callNativeArchiveCache(false, (bridge, token) => bridge.deleteArchiveCacheForIdentity?.(token, sourceCircleIdentityId) === true);
  }
};

export function canUseNativeChatEpochKeyStore(): boolean {
  return isWebViewMode
    && hasNativeCapability('nativeChatEpochKeysV1')
    && typeof window.AndroidKeyBridge?.saveChatEpochKey === 'function'
    && typeof window.AndroidKeyBridge?.getChatEpochKey === 'function';
}

export function saveNativeChatEpochKey(
  scope: 'group' | 'direct',
  chatId: string,
  epoch: number,
  rawKeyBase64: string
): boolean {
  if (!canUseNativeChatEpochKeyStore()) return false;
  try {
    return window.AndroidKeyBridge!.saveChatEpochKey!(getSessionToken(), scope, chatId, epoch, rawKeyBase64) === true;
  } catch {
    return false;
  }
}

export function getNativeChatEpochKey(
  scope: 'group' | 'direct',
  chatId: string,
  epoch: number
): string | null {
  if (!canUseNativeChatEpochKeyStore()) return null;
  try {
    const value = window.AndroidKeyBridge!.getChatEpochKey!(getSessionToken(), scope, chatId, epoch);
    return value ? value : null;
  } catch {
    return null;
  }
}

export function deleteNativeChatEpochKey(
  scope: 'group' | 'direct',
  chatId: string,
  epoch: number
): boolean {
  if (!canUseNativeChatEpochKeyStore() || typeof window.AndroidKeyBridge?.deleteChatEpochKey !== 'function') return false;
  try {
    return window.AndroidKeyBridge.deleteChatEpochKey(getSessionToken(), scope, chatId, epoch) === true;
  } catch {
    return false;
  }
}

export function canUseNativeTemporaryMembershipStore(): boolean {
  return isWebViewMode
    && hasNativeCapability('nativeTemporaryMembershipsV1')
    && typeof window.AndroidKeyBridge?.saveTemporaryMembership === 'function'
    && typeof window.AndroidKeyBridge?.getTemporaryMemberships === 'function';
}

export function saveNativeTemporaryMembership(enrollmentId: string, recordJson: string): boolean {
  if (!canUseNativeTemporaryMembershipStore()) return false;
  try {
    return window.AndroidKeyBridge!.saveTemporaryMembership!(getSessionToken(), enrollmentId, recordJson) === true;
  } catch {
    return false;
  }
}

export function loadNativeTemporaryMembershipsJson(): string | null {
  if (!canUseNativeTemporaryMembershipStore()) return null;
  try {
    return window.AndroidKeyBridge!.getTemporaryMemberships!(getSessionToken()) || '{}';
  } catch {
    return null;
  }
}

export function deleteNativeTemporaryMembership(enrollmentId: string): boolean {
  if (!canUseNativeTemporaryMembershipStore() || typeof window.AndroidKeyBridge?.deleteTemporaryMembership !== 'function') return false;
  try {
    return window.AndroidKeyBridge.deleteTemporaryMembership(getSessionToken(), enrollmentId) === true;
  } catch {
    return false;
  }
}

export function canUseNativeMobilePushRuntime(): boolean {
  return isWebViewMode
    && hasNativeCapability('nativeMobilePushRuntimeV1')
    && typeof window.AndroidKeyBridge?.refreshMobilePushRegistration === 'function';
}

export function refreshNativeMobilePushRegistration(): boolean {
  if (!canUseNativeMobilePushRuntime()) return false;
  try {
    return window.AndroidKeyBridge!.refreshMobilePushRegistration!(getSessionToken()) === true;
  } catch {
    return false;
  }
}

export function saveNativeMobileRefreshGrantJson(grantJson: string): boolean {
  if (!canUseNativeMobilePushRuntime() || typeof window.AndroidKeyBridge?.saveMobileRefreshGrant !== 'function') return false;
  try {
    return window.AndroidKeyBridge.saveMobileRefreshGrant(getSessionToken(), grantJson) === true;
  } catch {
    return false;
  }
}

export function loadNativeMobileRefreshGrantJson(): string | null {
  if (!canUseNativeMobilePushRuntime() || typeof window.AndroidKeyBridge?.getMobileRefreshGrant !== 'function') return null;
  try {
    return window.AndroidKeyBridge.getMobileRefreshGrant(getSessionToken()) || null;
  } catch {
    return null;
  }
}

export function clearNativeMobileRefreshGrant(): boolean {
  if (!canUseNativeMobilePushRuntime() || typeof window.AndroidKeyBridge?.clearMobileRefreshGrant !== 'function') return false;
  try {
    return window.AndroidKeyBridge.clearMobileRefreshGrant(getSessionToken()) === true;
  } catch {
    return false;
  }
}

export function saveNativeMobileServerTokenValidityJson(validityJson: string): boolean {
  if (!canUseNativeMobilePushRuntime() || typeof window.AndroidKeyBridge?.saveMobileServerTokenValidity !== 'function') return false;
  try {
    return window.AndroidKeyBridge.saveMobileServerTokenValidity(getSessionToken(), validityJson) === true;
  } catch {
    return false;
  }
}

export function loadNativeMobileServerTokenValidityJson(): string | null {
  if (!canUseNativeMobilePushRuntime() || typeof window.AndroidKeyBridge?.getMobileServerTokenValidity !== 'function') return null;
  try {
    return window.AndroidKeyBridge.getMobileServerTokenValidity(getSessionToken()) || '{}';
  } catch {
    return null;
  }
}

// In-memory cache of keys sent to/received from bridge.
// Kept in sync so that saveKeys always sends the full current state.
let _cache: BridgeKeyData | null = null;

function getCache(): BridgeKeyData {
  if (_cache) return _cache;
  _cache = { masterKey: '', identities: [], devices: [] };
  return _cache;
}

function readBridgeState(): BridgeKeyData | null {
  if (!isWebViewMode || !window.AndroidKeyBridge) return null;
  try {
    const json = window.AndroidKeyBridge.getStoredKeys(getSessionToken());
    const data = JSON.parse(json) as BridgeKeyData;
    const normalized: BridgeKeyData = {
      masterKey: typeof data.masterKey === 'string' ? data.masterKey : '',
      identities: Array.isArray(data.identities) ? data.identities : [],
      devices: Array.isArray(data.devices) ? data.devices : []
    };
    if (!normalized.masterKey && normalized.identities.length === 0 && normalized.devices.length === 0) {
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
}

function getMutableBridgeCache(): BridgeKeyData {
  const latest = readBridgeState();
  if (latest) {
    _cache = latest;
    return latest;
  }
  return getCache();
}

/**
 * Returns true if Android keystore actually has key_data stored.
 * This is intentionally boolean-only and does not expose key material. It helps
 * distinguish "no keys ever saved" from "keys exist but could not be read".
 */
export function androidHasStoredKeys(): boolean {
  if (!isWebViewMode || !window.AndroidKeyBridge) return false;
  try {
    return window.AndroidKeyBridge.hasKeys();
  } catch {
    return false;
  }
}

/**
 * Load key material from Android bridge.
 * Returns null if bridge is unavailable or has no keys stored yet.
 */
export function loadKeysFromBridge(): BridgeKeyData | null {
  const latest = readBridgeState();
  if (!latest) return null;
  _cache = latest;
  return latest;
}

export function consumeMobileHandoffFromBridge(): BridgeMobileHandoffData | null {
  if (!isWebViewMode || !window.AndroidKeyBridge) return null;
  try {
    const json = window.AndroidKeyBridge.consumeMobileHandoff(getSessionToken());
    const data = JSON.parse(json) as Partial<BridgeMobileHandoffData>;
    const handoffId = String(data.handoffId || '').trim();
    const handoffCode = String(data.handoffCode || '').trim();
    if (!handoffId || !handoffCode) return null;
    return { handoffId, handoffCode };
  } catch {
    return null;
  }
}

export function getAppLanguageFromBridge(): string | null {
  if (!isWebViewMode || !window.AndroidKeyBridge?.getAppLanguage) return null;
  try {
    const value = window.AndroidKeyBridge.getAppLanguage().trim().toLowerCase();
    return value || null;
  } catch {
    return null;
  }
}

export function setAppLanguageInBridge(language: string): boolean {
  if (!isWebViewMode || !window.AndroidKeyBridge?.setAppLanguage) return false;
  try {
    return window.AndroidKeyBridge.setAppLanguage(language);
  } catch {
    return false;
  }
}

/**
 * Save full key state to Android bridge.
 * Called after any key material update.
 */
function flushToBridge(): void {
  if (!isWebViewMode || !window.AndroidKeyBridge) return;
  const cache = getCache();
  try {
    window.AndroidKeyBridge.saveKeys(getSessionToken(), JSON.stringify(cache));
  } catch {
    // ignore
  }
}

/**
 * Update the master key stored in Android bridge.
 * Call after saveMasterKey() in WebView mode.
 */
export function updateBridgeMasterKey(rawBytes: Uint8Array): void {
  if (!isWebViewMode) return;
  const cache = getMutableBridgeCache();
  cache.masterKey = bytesToBase64(rawBytes);
  flushToBridge();
}

export function clearBridgeMasterKey(): void {
  if (!isWebViewMode) return;
  const cache = getMutableBridgeCache();
  cache.masterKey = '';
  flushToBridge();
}

export function clearNativeLocalProfileKeyMaterial(): boolean {
  if (!isWebViewMode || typeof window.AndroidKeyBridge?.clearLocalProfileKeyMaterial !== 'function') return false;
  try {
    return window.AndroidKeyBridge.clearLocalProfileKeyMaterial(getSessionToken()) === true;
  } catch {
    return false;
  }
}

/**
 * Add or update an identity seed (and optional metadata) in Android bridge.
 * Call after saveIdentity() in WebView mode.
 * If meta is omitted, existing metadata fields are preserved.
 */
export function updateBridgeIdentity(
  identityId: string,
  seed: Uint8Array,
  meta?: { publicKey: string; serverUrl: string; serverName?: string; identityName?: string; publishIdentity?: boolean; presenceVisible?: boolean; role?: string; accessKind?: 'direct_guest' | 'group_guest'; accessContextLabel?: string; vpsId?: string; isServerAdmin?: boolean; isPrimaryVpsTarget?: boolean; fingerprint?: string; serverId?: string; noNamesOnServer?: boolean; useForBackupStorage?: boolean; pendingRegistration?: boolean }
): void {
  if (!isWebViewMode) return;
  const cache = getMutableBridgeCache();
  const existing = cache.identities.find((i) => i.id === identityId);
  const entry: BridgeIdentityEntry = {
    id: identityId,
    seed: bytesToBase64(seed),
    publicKey: meta?.publicKey ?? existing?.publicKey ?? '',
    serverUrl: meta?.serverUrl ?? existing?.serverUrl ?? '',
    serverName: meta?.serverName ?? existing?.serverName,
    identityName: meta?.identityName ?? existing?.identityName,
    publishIdentity: meta?.publishIdentity ?? existing?.publishIdentity,
    presenceVisible: meta?.presenceVisible ?? existing?.presenceVisible,
    role: meta?.role ?? existing?.role,
    accessKind: meta?.accessKind ?? existing?.accessKind,
    accessContextLabel: meta?.accessContextLabel ?? existing?.accessContextLabel,
    vpsId: meta?.vpsId ?? existing?.vpsId,
    isServerAdmin: meta?.isServerAdmin ?? existing?.isServerAdmin,
    isPrimaryVpsTarget: meta?.isPrimaryVpsTarget ?? existing?.isPrimaryVpsTarget,
    fingerprint: meta?.fingerprint ?? existing?.fingerprint,
    serverId: meta?.serverId ?? existing?.serverId,
    noNamesOnServer: meta?.noNamesOnServer ?? existing?.noNamesOnServer,
    useForBackupStorage: meta?.useForBackupStorage ?? existing?.useForBackupStorage,
    pendingRegistration: meta?.pendingRegistration ?? existing?.pendingRegistration,
  };
  const idx = cache.identities.findIndex((i) => i.id === identityId);
  if (idx >= 0) {
    cache.identities[idx] = entry;
  } else {
    cache.identities.push(entry);
  }
  flushToBridge();
}

export function patchBridgeIdentityMeta(
  identityId: string,
  patch: Partial<Pick<BridgeIdentityEntry, 'publicKey' | 'serverUrl' | 'serverName' | 'identityName' | 'publishIdentity' | 'presenceVisible' | 'role' | 'accessKind' | 'accessContextLabel' | 'vpsId' | 'isServerAdmin' | 'isPrimaryVpsTarget' | 'fingerprint' | 'serverId' | 'noNamesOnServer' | 'useForBackupStorage' | 'pendingRegistration'>>
): void {
  if (!isWebViewMode) return;
  const cache = getMutableBridgeCache();
  const idx = cache.identities.findIndex((i) => i.id === identityId);
  if (idx < 0) return;

  const current = cache.identities[idx];
  cache.identities[idx] = {
    ...current,
    ...patch
  };
  flushToBridge();
}

/**
 * Remove an identity seed from Android bridge.
 * Call after deleteIdentity() in WebView mode.
 */
export function removeBridgeIdentity(identityId: string): void {
  if (!isWebViewMode) return;
  const cache = getMutableBridgeCache();
  cache.identities = cache.identities.filter((i) => i.id !== identityId);
  flushToBridge();
}

export function updateBridgeDevice(
  deviceId: string,
  identityId: string,
  publicKeyBase64: string,
  seed: Uint8Array,
  label?: string,
  encryptionPublicKeyBase64?: string,
  encryptionSeed?: Uint8Array,
  pendingRegistration?: boolean
): void {
  if (!isWebViewMode) return;
  const cache = getMutableBridgeCache();
  const entry: BridgeDeviceEntry = {
    deviceId,
    identityId,
    publicKey: publicKeyBase64,
    seed: bytesToBase64(seed),
    label,
    encryptionPublicKey: encryptionPublicKeyBase64,
    encryptionSeed: encryptionSeed ? bytesToBase64(encryptionSeed) : undefined,
    pendingRegistration
  };
  const idx = cache.devices.findIndex((d) => d.deviceId === deviceId);
  if (idx >= 0) {
    cache.devices[idx] = entry;
  } else {
    cache.devices.push(entry);
  }
  flushToBridge();
}

export function removeBridgeDevice(deviceId: string): void {
  if (!isWebViewMode) return;
  const cache = getMutableBridgeCache();
  cache.devices = cache.devices.filter((d) => d.deviceId !== deviceId);
  flushToBridge();
}

/**
 * Decode bridge key data into raw byte arrays for use in crypto operations.
 */
export function decodeBridgeData(data: BridgeKeyData): {
  masterKeyBytes: Uint8Array | null;
  identitySeeds: Map<string, Uint8Array>;
  deviceSeeds: Map<string, { identityId: string; publicKey: string; seed: Uint8Array; label?: string; encryptionPublicKey?: string; encryptionSeed?: Uint8Array; pendingRegistration?: boolean }>;
} {
  return {
    masterKeyBytes: data.masterKey ? base64ToBytes(data.masterKey) : null,
    identitySeeds: new Map(data.identities.map((i) => [i.id, base64ToBytes(i.seed)])),
    deviceSeeds: new Map(data.devices.map((d) => [d.deviceId, {
      identityId: d.identityId,
      publicKey: d.publicKey,
      seed: base64ToBytes(d.seed),
      label: d.label,
      encryptionPublicKey: d.encryptionPublicKey,
      encryptionSeed: d.encryptionSeed ? base64ToBytes(d.encryptionSeed) : undefined,
      pendingRegistration: d.pendingRegistration
    }])),
  };
}


export function hasMultipleNativeCameras(): boolean {
  if (!isWebViewMode) return false;
  try {
    return window.AndroidCallBridge?.hasMultipleCameras?.() === true;
  } catch {
    return false;
  }
}

export type NativeAudioCallBridgeRequest = {
  callSessionId: CallSessionId;
  remoteIdentityId: IdentityId;
  remoteDisplayName?: string;
  remoteIdentityPublicKey?: PublicKey;
  localIdentityId?: IdentityId;
  localVideoEnabled?: boolean;
  runtimeToken: CreateCallRuntimeTokenResponse;
  incomingOffer?: RTCSessionDescriptionInit & { dtlsSignature?: string };
};

export type NativeAudioCallBridgeEvent =
  | {
      type: 'state';
      callSessionId: CallSessionId;
      state: string;
      muted?: boolean;
      runtimeMode?: string;
      localVideoEnabled?: boolean;
      remoteVideoAvailable?: boolean;
      hadConnected?: boolean;
      connectedAtMs?: number;
    }
  | {
      type: 'ended';
      callSessionId: CallSessionId;
      reason: string;
    }
  | {
      type: 'upgrade-to-video';
      callSessionId: CallSessionId;
    };

export type NativeAudioRoute = {
  id: 'earpiece' | 'speaker' | 'wired' | 'bluetooth' | string;
  label: string;
  selected: boolean;
};

function parseBridgeResult(raw: string): void {
  if (!raw) return;
  const parsed = JSON.parse(raw) as { ok?: boolean; error?: string };
  if (parsed.ok === false) {
    throw new Error(parsed.error || 'Native call bridge request failed');
  }
}

export function ensureWebViewMediaPermissions(params: { audio: boolean; video: boolean }): void {
  if (!isWebViewMode) return;
  const raw = window.AndroidCallBridge?.ensureWebMediaPermissions(params.audio, params.video);
  parseBridgeResult(raw || '');
}

export function hasWebViewMediaPermissions(params: { audio: boolean; video: boolean }): boolean {
  if (!isWebViewMode) return true;
  return window.AndroidCallBridge?.hasWebMediaPermissions?.(params.audio, params.video) === true;
}

export function prepareWebViewMediaCapture(params: { audio: boolean; video: boolean }): void {
  if (!isWebViewMode) return;
  const raw = window.AndroidCallBridge?.prepareWebMediaCapture?.(params.audio, params.video);
  parseBridgeResult(raw || '');
}

export function startOutgoingNativeVideoCall(request: NativeAudioCallBridgeRequest): void {
  const raw = window.AndroidCallBridge?.startOutgoingVideoCall?.(JSON.stringify(request));
  parseBridgeResult(raw || JSON.stringify({ ok: false, error: 'Native video runtime is not available' }));
}

export function answerIncomingNativeVideoCall(request: NativeAudioCallBridgeRequest): void {
  const raw = window.AndroidCallBridge?.answerIncomingVideoCall?.(JSON.stringify(request));
  parseBridgeResult(raw || JSON.stringify({ ok: false, error: 'Native video runtime is not available' }));
}

export function endNativeCall(callSessionId: CallSessionId): void {
  window.AndroidCallBridge?.endCall(callSessionId);
}

export function setNativeCallMuted(callSessionId: CallSessionId, muted: boolean): void {
  window.AndroidCallBridge?.setMuted(callSessionId, muted);
}

export function setNativeCallLocalVideoEnabled(callSessionId: CallSessionId, enabled: boolean): void {
  window.AndroidCallBridge?.setLocalVideoEnabled?.(callSessionId, enabled);
}

export function switchNativeCallCamera(callSessionId: CallSessionId): void {
  const raw = window.AndroidCallBridge?.switchCamera?.(callSessionId);
  parseBridgeResult(raw || '');
}

export function stopIncomingNativeRinging(callSessionId?: CallSessionId | string | null): void {
  window.AndroidCallBridge?.stopIncomingRinging?.(callSessionId || undefined);
}

export function getNativeAudioRoutes(callSessionId: CallSessionId): NativeAudioRoute[] {
  if (!isWebViewMode) return [];
  try {
    const raw = window.AndroidCallBridge?.getAudioRoutes(callSessionId) || '[]';
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((route) => ({
      id: String(route?.id || '').trim(),
      label: String(route?.label || '').trim(),
      selected: route?.selected === true
    })).filter((route) => route.id);
  } catch {
    return [];
  }
}

export function setNativeAudioRoute(callSessionId: CallSessionId, routeId: string): void {
  const raw = window.AndroidCallBridge?.setAudioRoute(callSessionId, routeId);
  parseBridgeResult(raw || '');
}

export function setNativeAudioRouteDialogOpen(open: boolean): void {
  if (!isWebViewMode) return;
  window.AndroidCallBridge?.setNativeAudioRouteDialogOpen?.(open);
}

export function resumeNativeCall(): void {
  if (!isWebViewMode) return;
  window.AndroidCallBridge?.resumeNativeCall?.();
}

export function consumeNativeCallMinimizedLaunchPending(): boolean {
  if (!isWebViewMode) return false;
  try {
    return window.AndroidCallBridge?.consumeNativeCallMinimizedLaunchPending?.() === true;
  } catch {
    return false;
  }
}

export type ActiveNativeCallSnapshot = {
  callSessionId: string;
  state: string;
  runtimeMode: string;
  muted: boolean;
  localVideoEnabled: boolean;
  remoteVideoAvailable: boolean;
  hadConnected: boolean;
  connectedAtMs: number;
  remoteIdentityId: string;
  remoteDisplayName: string;
  localIdentityId: string;
};

export function getActiveNativeCalls(): ActiveNativeCallSnapshot[] {
  if (!isWebViewMode) return [];
  try {
    const raw = window.AndroidCallBridge?.getActiveCalls?.() || '[]';
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item: Record<string, unknown>) => ({
        callSessionId: String(item?.callSessionId || '').trim(),
        state: String(item?.state || '').trim(),
        runtimeMode: String(item?.runtimeMode || 'video-native').trim(),
        muted: item?.muted === true,
        localVideoEnabled: item?.localVideoEnabled === true,
        remoteVideoAvailable: item?.remoteVideoAvailable === true,
        hadConnected: item?.hadConnected === true,
        connectedAtMs: Number(item?.connectedAtMs || 0),
        remoteIdentityId: String(item?.remoteIdentityId || '').trim(),
        remoteDisplayName: String(item?.remoteDisplayName || '').trim(),
        localIdentityId: String(item?.localIdentityId || '').trim()
      }))
      .filter((c) => c.callSessionId && c.remoteIdentityId);
  } catch {
    return [];
  }
}

// ─── Contact bridge ───────────────────────────────────────────────────────────

/**
 * Serialize a VaultContact into the shape expected by AndroidContactBridge.
 * Only fields needed by the native layer are included.
 */
function serializeContactForBridge(contact: VaultContact): object {
  return {
    contactId: contact.contactId,
    displayName: contact.displayName,
    identities: contact.identities.map((id) => ({
      serverId: id.serverId,
      serverUrl: id.serverUrl,
      identityId: id.identityId,
      publicKeyAlgorithm: id.identityPublicKey.algorithm,
      publicKeyValue: id.identityPublicKey.value
    }))
  };
}

/** Sync a single contact addition or update to native ContactStore. */
export function syncContactToBridge(contact: VaultContact): void {
  if (!isWebViewMode || !hasNativeCapability('nativeContactIndexV1') || !window.AndroidContactBridge) return;
  try {
    window.AndroidContactBridge.upsertContact(JSON.stringify(serializeContactForBridge(contact)));
  } catch {
    // ignore — native sync is best-effort
  }
}

/** Sync a contact deletion to native ContactStore. */
export function deleteContactFromBridge(contactId: string): void {
  if (!isWebViewMode || !hasNativeCapability('nativeContactIndexV1') || !window.AndroidContactBridge) return;
  try {
    window.AndroidContactBridge.deleteContact(contactId);
  } catch {
    // ignore
  }
}

/** Clear native-only contact metadata when the entire local profile is removed. */
export function clearNativeContactData(): boolean {
  if (!isWebViewMode || typeof window.AndroidContactBridge?.clearAllContacts !== 'function') return false;
  try {
    return window.AndroidContactBridge.clearAllContacts() === true;
  } catch {
    return false;
  }
}

/** Full replace — call after vault load or vault merge from server. */
export function syncAllContactsToBridge(contacts: VaultContact[]): void {
  if (!isWebViewMode || !hasNativeCapability('nativeContactIndexV1') || !window.AndroidContactBridge) return;
  try {
    window.AndroidContactBridge.replaceAllContacts(
      JSON.stringify(contacts.map((c) => serializeContactForBridge(c)))
    );
  } catch {
    // ignore
  }
}
